import React, { useState } from "react";
import { Button } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import { splitQuestionTitle } from "./questionTitle";
import AssessmentUpliftHeader from "./AssessmentUpliftHeader";
import ProgressPill, { ProgressPillProps } from "./ProgressPill";
import AssessmentStepTransition from "./AssessmentStepTransition";
import styles from "./AssessmentQuestionUplift.module.scss";
import durationStyles from "./AssessmentQuestionDurationUplift.module.scss";

export interface AssessmentQuestionDurationUpliftProps {
  stepKey: string;
  titleHtml?: string | null;
  subtitleHtml?: string | null;
  defaultHours?: number;
  defaultMinutes?: number;
  onSubmit: (hours: number, minutes: number) => void;
  onBack?: () => void;
  onClose: () => void;
  progressPill?: ProgressPillProps | null;
}

const HOURS_MAX = 99;
const MINUTES_MAX = 45;
const MINUTES_STEP = 15;
const MAX_DIGITS = 2;

// Backend copy sometimes uses a curly apostrophe ("doesn’t"); normalize both
// sides before comparing so authored duplicates of this hint are detected.
const normalizeApostrophes = (value: string) => value.replace(/[‘’]/g, "'");

const AssessmentQuestionDurationUplift: React.FC<
  AssessmentQuestionDurationUpliftProps
> = ({
  stepKey,
  titleHtml,
  subtitleHtml,
  defaultHours = 0,
  defaultMinutes = 0,
  onSubmit,
  onBack,
  onClose,
  progressPill,
}) => {
  const { t } = useTranslation(["assessment"]);
  const BEST_GUESS_HINT = t("assessment:question.estimateHint");
  const { eyebrow, title } = splitQuestionTitle(titleHtml);
  const rawQuestionHint = subtitleHtml
    ? subtitleHtml.replace(/<[^>]+>/g, "").trim()
    : null;
  // Some questions (e.g. sleep duration) author the best-guess hint as their
  // own subtitleHtml. Don't repeat it above the steppers as well as below.
  const questionHint =
    rawQuestionHint &&
    normalizeApostrophes(rawQuestionHint.toLowerCase()) !==
      normalizeApostrophes(BEST_GUESS_HINT.toLowerCase())
      ? rawQuestionHint
      : null;

  const [hours, setHours] = useState(defaultHours);
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [hoursText, setHoursText] = useState(String(defaultHours));
  const [minutesText, setMinutesText] = useState(String(defaultMinutes));

  const commitHours = (next: number) => {
    setHours(next);
    setHoursText(String(next));
  };
  const commitMinutes = (next: number) => {
    setMinutes(next);
    setMinutesText(String(next));
  };

  const decrementHours = () => commitHours(Math.max(0, hours - 1));
  const incrementHours = () => commitHours(Math.min(HOURS_MAX, hours + 1));
  // Minutes only take 15-minute-step values, but manual typing can leave an
  // off-step value (e.g. 5) committed on blur — snap to the nearest step
  // boundary in the increment/decrement direction rather than adding/
  // subtracting MINUTES_STEP from whatever was typed.
  const decrementMinutes = () =>
    commitMinutes(Math.max(0, Math.ceil(minutes / MINUTES_STEP) * MINUTES_STEP - MINUTES_STEP));
  const incrementMinutes = () =>
    commitMinutes(
      Math.min(MINUTES_MAX, Math.floor(minutes / MINUTES_STEP) * MINUTES_STEP + MINUTES_STEP)
    );

  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setHoursText(
      event.target.value.replace(/[^0-9]/g, "").slice(0, MAX_DIGITS)
    );
  const handleHoursBlur = () =>
    commitHours(
      hoursText === ""
        ? 0
        : Math.min(HOURS_MAX, Math.max(0, Number(hoursText)))
    );

  const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setMinutesText(
      event.target.value.replace(/[^0-9]/g, "").slice(0, MAX_DIGITS)
    );
  const handleMinutesBlur = () =>
    commitMinutes(
      minutesText === ""
        ? 0
        : Math.min(MINUTES_MAX, Math.max(0, Number(minutesText)))
    );

  const handleContinue = () => onSubmit(hours, minutes);

  return (
    <div
      className={styles.container}
      data-testid="assessment-question-duration-uplift"
    >
      <AssessmentUpliftHeader onBack={onBack} onClose={onClose} />

      {progressPill && (
        <div className={styles["pill-row"]}>
          <ProgressPill {...progressPill} />
        </div>
      )}

      <div className={styles.body}>
        <AssessmentStepTransition stepKey={stepKey}>
          <div className={styles.content}>
            {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
            <h1 className={styles.title}>{title}</h1>
            {questionHint && (
              <p className={durationStyles["question-hint"]}>
                {questionHint}
              </p>
            )}

            <div
              className={durationStyles.stepper}
              data-testid="assessment-duration-hours"
            >
              <button
                type="button"
                aria-label="decrease hours"
                className={durationStyles["stepper-button"]}
                disabled={hours <= 0}
                onClick={decrementHours}
              >
                {"−"}
              </button>
              <div className={durationStyles["stepper-value-group"]}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={MAX_DIGITS}
                  aria-label="hours value"
                  className={durationStyles["stepper-value"]}
                  value={hoursText}
                  onChange={handleHoursChange}
                  onBlur={handleHoursBlur}
                />
                <p className={durationStyles["stepper-label"]}>
                  {t("assessment:duration.hoursLabel")}
                </p>
              </div>
              <button
                type="button"
                aria-label="increase hours"
                className={durationStyles["stepper-button"]}
                disabled={hours >= HOURS_MAX}
                onClick={incrementHours}
              >
                {"+"}
              </button>
            </div>

            <div
              className={durationStyles.stepper}
              data-testid="assessment-duration-minutes"
            >
              <button
                type="button"
                aria-label="decrease minutes"
                className={durationStyles["stepper-button"]}
                disabled={minutes <= 0}
                onClick={decrementMinutes}
              >
                {"−"}
              </button>
              <div className={durationStyles["stepper-value-group"]}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={MAX_DIGITS}
                  aria-label="minutes value"
                  className={durationStyles["stepper-value"]}
                  value={minutesText}
                  onChange={handleMinutesChange}
                  onBlur={handleMinutesBlur}
                />
                <p className={durationStyles["stepper-label"]}>
                  {t("assessment:duration.minutesLabel")}
                </p>
              </div>
              <button
                type="button"
                aria-label="increase minutes"
                className={durationStyles["stepper-button"]}
                disabled={minutes >= MINUTES_MAX}
                onClick={incrementMinutes}
              >
                {"+"}
              </button>
            </div>

            <p className={durationStyles.hint}>{BEST_GUESS_HINT}</p>

            <Button
              variant="enclosed-primary"
              monochromeColour="black"
              className={durationStyles["continue-button"]}
              onClick={handleContinue}
            >
              {t("assessment:question.continue")}
            </Button>
          </div>
        </AssessmentStepTransition>
      </div>
    </div>
  );
};

export default AssessmentQuestionDurationUplift;
